/**
 * Estado compartilhado do Planner Semanal TPL — usado pelo Construtor e Meu Padrão (dia).
 * Recorrência alinhada a isTemplateDueOnDate (lstSchedule) — mesma regra da Matriz Kamishibai.
 */

import type { LstTemplate } from '../types';
import { formatTimeHHmm } from './lstMatrixHourly';
import { isTemplateDueOnDate, templateWeekdayMon0 } from './lstSchedule';
import { weekdayIndexMon0 } from './lstService';

export const PLANNER_HOURS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
  '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
] as const;

export const PLANNER_WEEKDAYS = [
  { id: 1, label: 'Segunda' },
  { id: 2, label: 'Terça' },
  { id: 3, label: 'Quarta' },
  { id: 4, label: 'Quinta' },
  { id: 5, label: 'Sexta' },
] as const;

export interface PlannerBlock {
  id: string;
  templateId: string;
  title: string;
  day: number;
  hour: string;
  durationMinutes: number;
  category: string;
}

export function snapHour(time: string): string {
  const match = PLANNER_HOURS.find((h) => h === time);
  if (match) return match;
  const [h] = time.split(':').map(Number);
  const found = PLANNER_HOURS.find((slot) => parseInt(slot, 10) === h);
  return found ?? '08:00';
}

function isDailyLike(freq: LstTemplate['frequency']): boolean {
  return freq === 'horaria' || freq === 'diaria';
}

/** Dia do planner (1=Seg … 5=Sex) para rotinas semanais. */
export function plannerDayForTemplate(template: LstTemplate, index: number): number {
  if (template.frequency === 'semanal') {
    const wd = templateWeekdayMon0(template);
    return wd >= 5 ? 5 : wd + 1;
  }
  const anchor = template.sort_order > 0 ? template.sort_order : index + 1;
  return ((anchor - 1) % 5) + 1;
}

/** Dia do planner correspondente a uma data ISO (null = fim de semana). */
export function plannerDayFromDate(iso: string): number | null {
  const wd = weekdayIndexMon0(iso);
  if (wd >= 5) return null;
  return wd + 1;
}

function makeBlock(
  template: LstTemplate,
  day: number,
  hour: string,
  suffix: string,
): PlannerBlock {
  return {
    id: `${template.id}-${suffix}`,
    templateId: template.id,
    title: template.title,
    day,
    hour,
    durationMinutes: template.duration_minutes ?? 15,
    category: template.category ?? 'apoio',
  };
}

/**
 * Blocos do planner derivados dos templates — mesma recorrência da Matriz Kamishibai.
 * Diárias/horárias: um bloco por dia útil (Seg–Sex).
 * Semanais: um bloco no dia da semana âncora.
 * Mensais: bloco em cada dia útil da semana em que a rotina vence.
 */
export function buildPlannerBlocks(
  templates: LstTemplate[],
  weekDates: string[] = [],
): PlannerBlock[] {
  const active = templates.filter((t) => t.active);
  const blocks: PlannerBlock[] = [];

  for (const [i, t] of active.entries()) {
    const hour = snapHour(formatTimeHHmm(t.scheduled_time) ?? '08:00');

    if (isDailyLike(t.frequency)) {
      for (const wd of PLANNER_WEEKDAYS) {
        blocks.push(makeBlock(t, wd.id, hour, `d${wd.id}`));
      }
      continue;
    }

    if (t.frequency === 'semanal') {
      blocks.push(makeBlock(t, plannerDayForTemplate(t, i), hour, 'w'));
      continue;
    }

    if (t.frequency === 'mensal' && weekDates.length > 0) {
      let added = false;
      for (const iso of weekDates) {
        if (!isTemplateDueOnDate(t, iso, weekDates)) continue;
        const day = plannerDayFromDate(iso);
        if (day === null) continue;
        blocks.push(makeBlock(t, day, hour, `m${iso}`));
        added = true;
      }
      if (added) continue;
    }

    blocks.push(makeBlock(t, plannerDayForTemplate(t, i), hour, 'x'));
  }

  return blocks;
}

/** Quantidade de blocos do planner para um template (diárias = 5 dias úteis). */
export function plannerBlockCountForTemplate(
  blocks: PlannerBlock[],
  templateId: string,
): number {
  return blocks.filter((b) => b.templateId === templateId).length;
}

/**
 * Rotina alocada na coluna do planner do dia corrente.
 * Diárias/horárias: todos os dias úteis; demais frequências seguem isTemplateDueOnDate.
 */
export function isTemplateOnPlannerDay(
  template: LstTemplate,
  blocks: PlannerBlock[],
  date: string,
): boolean {
  if (isDailyLike(template.frequency)) return true;

  const plannerDay = plannerDayFromDate(date);
  if (plannerDay === null) return false;

  if (!isTemplateDueOnDate(template, date, [date])) return false;

  const templateBlocks = blocks.filter((b) => b.templateId === template.id);
  if (templateBlocks.length === 0) return true;

  return templateBlocks.some((b) => b.day === plannerDay);
}

/** Ordenação alinhada ao grid do planner (horário fixo → bloco do turno). */
export function sortTemplatesByPlannerTime(
  a: LstTemplate,
  b: LstTemplate,
  blocks: PlannerBlock[],
): number {
  const minutesOf = (t: LstTemplate): number => {
    const block = blocks.find((x) => x.templateId === t.id);
    const time = block?.hour ?? formatTimeHHmm(t.scheduled_time) ?? '';
    if (time) {
      const [h, m] = time.split(':').map(Number);
      return h * 60 + m;
    }
    const base = { inicio: 360, meio: 600, fim: 780 }[t.period];
    return base + t.sort_order;
  };
  return minutesOf(a) - minutesOf(b) || a.sort_order - b.sort_order;
}

/**
 * Mescla overrides otimistas (drag-and-drop) sobre blocos derivados dos templates.
 * Rotinas com múltiplos blocos (diárias): sincroniza apenas o horário em todos os dias.
 */
export function mergePlannerBlocks(
  blocks: PlannerBlock[],
  overrides: Readonly<Record<string, { day: number; hour: string }>>,
): PlannerBlock[] {
  return blocks.map((b) => {
    const o = overrides[b.templateId];
    if (!o) return b;
    const multiDay = plannerBlockCountForTemplate(blocks, b.templateId) > 1;
    if (multiDay) {
      return { ...b, hour: o.hour };
    }
    return { ...b, day: o.day, hour: o.hour };
  });
}

export function findPlannerBlock(
  blocks: PlannerBlock[],
  templateId: string,
): PlannerBlock | undefined {
  return blocks.find((b) => b.templateId === templateId);
}
